import * as Sentry from '@sentry/react';
import { cloneDeep } from 'lodash';

const sanitizeReduxState = (state) => {
  if (!state) {
    return null;
  }

  const sanitizedState = cloneDeep(state);

  // Login Page
  if (sanitizedState.form?.login?.values?.password) {
    sanitizedState.form.login.values.password = 'REDACTED';
  }

  // Change Password flow in User Profile Page
  if (sanitizedState.form?.['app/UserPage/FORM_IDENTIFIER']?.values) {
    const { values } = sanitizedState.form?.['app/UserPage/FORM_IDENTIFIER'];

    if (values.oldPassword) {
      values.oldPassword = 'REDACTED';
    }

    if (values.newPassword) {
      values.newPassword = 'REDACTED';
    }

    if (values.newPassword2) {
      values.newPassword2 = 'REDACTED';
    }
  }

  return sanitizedState;
};

/** NOTE-DP: this is basically a clone of `createReduxEnhancer`, but as a middleware
 * `createReduxEnhancer` replaces the top level reducer, which then gets replaced with `reducerInjectors`, so it stops working almost immediately
 */
export const sentryReduxMiddleware = (middlewareOptions) => {
  Sentry.getCurrentScope().addEventProcessor((event, hint) => {
    try {
      if (event.type === undefined && event?.contexts?.state?.state?.type === 'redux') {
        const { value } = event.contexts.state.state;

        const sanitizedState = sanitizeReduxState(value);

        // eslint-disable-next-line no-param-reassign
        hint.attachments = [
          ...(hint.attachments || []),
          { filename: 'redux_state.json', data: JSON.stringify(sanitizedState) },
        ];
      }
    } catch (_) {
      // empty
    }
    return event;
  });

  return (store) => (next) => (action) => {
    const state = store.getState();

    const scope = Sentry.getCurrentScope();

    /* Action breadcrumbs */
    if (typeof action !== 'undefined' && action !== null) {
      scope.addBreadcrumb({
        category: 'redux.action',
        data: action,
        type: 'info',
      });
    }

    /* Set latest state to scope */
    const transformedState = middlewareOptions.stateTransformer(state);
    if (typeof transformedState !== 'undefined' && transformedState !== null) {
      const client = Sentry.getClient();
      const options = client && client.getOptions();
      const normalizationDepth = (options && options.normalizeDepth) || 3; // default state normalization depth to 3

      // Set the normalization depth of the redux state to the configured `normalizeDepth` option or a sane number as a fallback
      const newStateContext = { state: { type: 'redux', value: transformedState } };
      Object.defineProperty(newStateContext, '__sentry_override_normalization_depth__', {
        value: 3 + normalizationDepth,
        writable: true,
        configurable: true,
      });

      scope.setContext('state', newStateContext);
    } else {
      scope.setContext('state', null);
    }

    /* Allow user to configure scope with latest state */
    const { configureScopeWithState } = middlewareOptions;
    if (typeof configureScopeWithState === 'function') {
      configureScopeWithState(scope, state);
    }

    next(action);
  };
};
